import { type ReactElement } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const DEFAULT_PAGE_SIZE_ID = 'data-table-page-size';

export function DataTableToolbar({
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  from,
  to,
  total,
  onPagePrevious,
  onPageNext,
  canGoPrevious = false,
  canGoNext = false,
  pageSizeSelectId = DEFAULT_PAGE_SIZE_ID,
  className,
  buttonClassName,
}: {
  readonly pageSize: number;
  readonly pageSizeOptions?: readonly number[];
  readonly onPageSizeChange: (size: number) => void;
  readonly from: number;
  readonly to: number;
  readonly total: number;
  readonly onPagePrevious: () => void;
  readonly onPageNext: () => void;
  readonly canGoPrevious?: boolean;
  readonly canGoNext?: boolean;
  readonly pageSizeSelectId?: string;
  /** Tailwind override for the toolbar wrapper (e.g. trim `py-2.5`). */
  readonly className?: string;
  /** Tailwind override for the prev/next icon buttons (e.g. shrink h-8). */
  readonly buttonClassName?: string;
}): ReactElement {
  const { t } = useTranslation();
  const range = t('ui.dataTable.range', { from, to, total });

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-b border-border bg-white px-3 py-2.5 text-xs text-muted-foreground dark:bg-card sm:px-4',
        className,
      )}
      aria-label={t('ui.dataTable.pagerGroup')}
    >
      <div className="flex flex-wrap items-center gap-2 sm:gap-1.5">
        <span className="shrink-0 text-muted-foreground">
          {t('ui.dataTable.showPrefix')}
        </span>
        <label htmlFor={pageSizeSelectId} className="sr-only">
          {t('ui.dataTable.pageSizeLabel')}
        </label>
        <select
          id={pageSizeSelectId}
          className={cn(
            'h-7 min-w-[2.75rem] cursor-pointer rounded-md border border-border bg-white px-1.5 pr-5 text-center text-xs font-medium text-foreground',
            'shadow-sm dark:border-input dark:bg-card',
          )}
          value={String(pageSize)}
          onChange={(e) => {
            onPageSizeChange(Number.parseInt(e.target.value, 10));
          }}
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <span className="shrink-0 text-muted-foreground">
          {t('ui.dataTable.perPageSuffix')}
        </span>
      </div>
      <div className="flex min-w-0 items-center gap-0.5 sm:gap-1">
        <span
          className="mr-1.5 min-w-0 select-none text-muted-foreground sm:mr-2"
          aria-live="polite"
        >
          {range}
        </span>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('h-8 w-8 text-muted-foreground', buttonClassName)}
          onClick={onPagePrevious}
          disabled={!canGoPrevious}
          aria-label={t('ui.dataTable.previousPage')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('h-8 w-8 text-muted-foreground', buttonClassName)}
          onClick={onPageNext}
          disabled={!canGoNext}
          aria-label={t('ui.dataTable.nextPage')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
